import React from "react";
import { Box, Button, ButtonBase, Checkbox, CircularProgress, InputBase, TextField, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import {
  IoCardOutline,
} from "react-icons/io5";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import StripeTextField from "Pages/Components/Checkout/Stripe/StripeTextField";
import JGIcon from "Pages/Components/JGIcon";
import {
  calcCoverFee,
  DonationFrequency,
  DonorVariant,
  FONT_SANS,
  getAmountsForFrequency,
  getMembershipTooltip,
  GIFT_MINIMUM,
  PaymentMethod,
  PURPLE,
} from "./checkoutV2.constants";
import DonationSummaryV2 from "./DonationSummaryV2";

export interface DonationFormState {
  email: string;
  frequency: DonationFrequency;
  amount: number;
  otherAmount: string;
  paymentMethod: PaymentMethod;
  giftToSomeone: boolean;
  coverFees: boolean;
  autoRenew: boolean;
  /**
   * Billing ZIP for the card. Collected only in trial mode, where it's sent to
   * Stripe with the card for the AVS check — Stripe's split card elements don't
   * include a postal-code field of their own.
   */
  zip: string;
}

interface DonationFormProps {
  state: DonationFormState;
  onChange: <K extends keyof DonationFormState>(
    key: K,
    value: DonationFormState[K]
  ) => void;
  onSubmit: () => void;
  /** Selected amount, accounting for a typed "Other Amount". */
  effectiveAmount: number;
  /** Whether the conditional auto-renew checkbox should be shown. */
  showAutoRenew: boolean;
  /** Which top-of-form block to show, derived from the donor's Auth0 state. */
  variant: DonorVariant;
  /** Opens the Auth0 sign-in popup (email pre-filled from the entered value). */
  onSignIn: () => void;
  /** Fires the on-blur email-recognition lookup (existing account → Version 2). */
  onEmailBlur: () => void;
  /** True while the email-recognition lookup is in flight. */
  checkingEmail?: boolean;
  /** Clears the recognized email and returns to the email-entry block. */
  onUseDifferentEmail: () => void;
  /** Paid-but-not-registered donor finishes setting up their account. */
  onCompleteRegistration: () => void;
  /** True while the sign-in popup is open / completing. */
  signingIn?: boolean;
  /** Sign-in error message (e.g. popup closed/failed), or null. */
  authError?: string | null;
  /** Stripe card-field completeness callbacks (drive Submit enablement). */
  onCardChange?: (complete: boolean) => void;
  onExpiryChange?: (complete: boolean) => void;
  onCvcChange?: (complete: boolean) => void;
  /** ACH (Checking Account) via Plaid: whether Plaid Link is ready to open. */
  achReady?: boolean;
  /** The connected bank account's masked number, or null if none connected. */
  achConnectedMask?: string | null;
  /** Opens the Plaid Link modal to connect (or reconnect) a bank account. */
  onConnectBank?: () => void;
  /**
   * Free-trial signup (donate page opened with ?coupon=freetrial). The amount is
   * fixed at $10/month so the frequency toggle and amount grid are hidden, and a
   * billing ZIP is collected with the card. Nothing is charged today — the card
   * is stored and first billed 14 days from signup.
   */
  trialMode?: boolean;
  /** Below-minimum "Other Amount" message, shown under that field. */
  amountError?: string | null;
  /** True while the donation is submitting (tokenize/charge in flight). */
  submitting?: boolean;
  /** Submit/payment error message, or null. */
  submitError?: string | null;
  /** Signed-in donor's first name, for the "member" greeting. */
  donorName?: string;
  /** Whether the signed-in donor's membership has lapsed. */
  membershipInactive?: boolean;
  /** Formatted membership end date shown in the "Inactive Membership" copy. */
  membershipEndDate?: string | null;
  /**
   * When true (CheckoutV3 layout), the "Your Information" block is rendered
   * below the "Choose Donation Amount" block instead of above it.
   */
  infoBelowAmount?: boolean;
}

/** Purple uppercase heading with a trailing rule, as in the design. */
const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: {xs:"20px", md: 2} }}>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: {xs: "14px", md: "16px"},
        fontWeight: 700,
        letterSpacing: {xs: "1.2px", md: "1.6px"},
        lineHeight:{xs:1.2, md: 1.2},
        textTransform: "uppercase",
        color: PURPLE,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flexGrow: 1, height: "1px", backgroundColor: PURPLE}} />
  </Box>
);

/** Shared outlined input styling matching the mockup. */
const fieldSx = {
  width: "100%",
  // border: "1px solid #D9D7D0",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 4px 4px 0px #00000040",
  px: 2,
  py: 1.13,
  fontFamily: FONT_SANS,
  fontSize:{xs:"14px", md: "16px"},
  color: "#3F3F3F",
  lineHeight: 1,
  "&.Mui-focused": { borderColor: PURPLE },
  "& input::placeholder": { color: "#9A988F", opacity: 1 },
};

/** Styles a MUI Stripe field (StripeTextField) to match the white rounded
 *  `fieldSx` look: no visible outline, white background, soft shadow. */
const stripeFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0px 4px 4px 0px #00000040",
    fontFamily: FONT_SANS,
    fontSize: { xs: "14px", md: "16px" },
    color: "#3F3F3F",
    px: 2,
    py: 0.5,
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiFormHelperText-root": { mx: 1, mt: 0.5, minHeight: 0 },
};

/** Dark text/placeholder inside the Stripe iframe, to match the white fields
 *  (the shared StripeInput otherwise uses the dark-theme's light text color). */
const stripeElementOptions = {
  style: {
    base: {
      color: "#3F3F3F",
      "::placeholder": { color: "#9A988F" },
    },
    // Without this, StripeInput's default invalid color is the dark theme's
    // near-white text, which vanishes on these white fields (e.g. an incomplete
    // card number left on blur). Use the same red the form's error text uses.
    invalid: {
      color: "#D32F2F",
    },
  },
};
const stripeInputProps = { options: stripeElementOptions };

/** Inline "Sign in" affordance shared by the new-visitor and sign-in blocks. */
const SignInText = ({ onSignIn }: { onSignIn: () => void }) => {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      onClick={onSignIn}
      sx={{ color: PURPLE, fontWeight: 600, cursor: "pointer" }}
    >
      {t("donationPage.signIn")}
    </Box>
  );
};

/**
 * Default block for an anonymous first-time visitor: collect an email so we can
 * create their Jazz Groove account, with a path to sign in if they already have
 * one.
 */
const NewVisitorInfo = ({
  email,
  onEmailChange,
  onEmailBlur,
  onSignIn,
  signingIn,
  checking,
  error,
}: {
  email: string;
  onEmailChange: (value: string) => void;
  onEmailBlur: () => void;
  onSignIn: () => void;
  signingIn?: boolean;
  checking?: boolean;
  error?: string | null;
}) => {
  const { t } = useTranslation();
  return (
  <Box>
    <SectionHeading>{t("donationPage.yourInformation")}</SectionHeading>
    <InputBase
      value={email}
      onChange={(e) => onEmailChange(e.target.value)}
      onBlur={onEmailBlur}
      placeholder={t("donationPage.emailPlaceholder")}
      type="email"
      sx={fieldSx}
      endAdornment={
        checking ? (
          <CircularProgress size={18} sx={{ color: PURPLE, ml: 1 }} />
        ) : null
      }
    />
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: "14px",
        color: "#231F20",
        mt: 2,
        lineHeight: 1.5,
      }}
    >
      {t("donationPage.enterEmailPrompt")}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: "14px",
        color: "#231F20",
        lineHeight: 1.5,
      }}
    >
      {t("donationPage.alreadyHaveOne")}{" "}
      {signingIn ? (
        <Box component="span" sx={{ color: PURPLE, fontWeight: 600 }}>
          {t("donationPage.signingIn")}
        </Box>
      ) : (
        <SignInText onSignIn={onSignIn} />
      )}
    </Typography>
    {error && (
      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: "13px",
          color: "#D32F2F",
          mt: 1,
          lineHeight: 1.4,
        }}
      >
        {error}
      </Typography>
    )}
  </Box>
  );
};

/**
 * Version 2: once an entered email is recognized as an existing account, prompt
 * the donor to sign in (the button opens the Auth0 popup, email pre-filled).
 * No password field here — sign-in is via the popup.
 */
const RecognizedSignIn = ({
  email,
  onSignIn,
  onUseDifferentEmail,
  error,
  loading,
}: {
  email: string;
  onSignIn: () => void;
  onUseDifferentEmail: () => void;
  error?: string | null;
  loading?: boolean;
}) => {
  const { t } = useTranslation();
  return (
  <Box>
    <SectionHeading>{t("donationPage.yourInformation")}</SectionHeading>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: { xs: "16px", md: "18px" },
        fontWeight: 700,
        color: "#231F20",
        lineHeight: 1.4,
      }}
    >
      {t("donationPage.welcomeBack")}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: "14px",
        color: "#231F20",
        mt: 1,
        mb: 2,
        lineHeight: 1.5,
      }}
    >
      <Trans
        i18nKey="donationPage.recognizedSignInPrompt"
        values={{ email }}
        components={{ 1: <b /> }}
      />
    </Typography>

    {error && (
      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: "13px",
          color: "#D32F2F",
          mb: 1.5,
          lineHeight: 1.4,
        }}
      >
        {error}
      </Typography>
    )}

    <Button
      fullWidth
      onClick={onSignIn}
      disabled={loading}
      startIcon={
        loading ? <CircularProgress size={16} sx={{ color: "common.white" }} /> : null
      }
      sx={{
        backgroundColor: PURPLE,
        color: "common.white",
        fontFamily: FONT_SANS,
        fontSize: "16px",
        fontWeight: 700,
        textTransform: "none",
        borderRadius: "12px",
        background: PURPLE,
        py: 1.5,
        boxShadow: "0px 4px 4px 0px #00000040",
        "&:hover": { backgroundColor: PURPLE, boxShadow: "0px 4px 4px 0px #00000040" },
        // Re-assert the background: MUI's disabled styles drop it, which would
        // leave a transparent button while the sign-in is in flight.
        "&.Mui-disabled": {
          color: "common.white",
          opacity: 0.7,
          backgroundColor: PURPLE,
          background: PURPLE,
        },
      }}
    >
      {loading ? t("donationPage.signingIn") : t("donationPage.signIn")}
    </Button>

    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: "14px",
        color: "#231F20",
        mt: 2,
        lineHeight: 1.5,
      }}
    >
      {t("donationPage.notYou")}{" "}
      <Box
        component="span"
        onClick={onUseDifferentEmail}
        sx={{ color: PURPLE, fontWeight: 600, cursor: "pointer" }}
      >
        {t("donationPage.useDifferentEmail")}
      </Box>
    </Typography>
  </Box>
  );
};

/**
 * The email already donated (has a DynamoDB record) but never created a login —
 * prompt them to finish setting up their account (name + password) rather than
 * pay again or sign in.
 */
const CompleteRegistrationPrompt = ({
  email,
  onCompleteRegistration,
  onUseDifferentEmail,
}: {
  email: string;
  onCompleteRegistration: () => void;
  onUseDifferentEmail: () => void;
}) => {
  const { t } = useTranslation();
  return (
  <Box>
    <SectionHeading>{t("donationPage.yourInformation")}</SectionHeading>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: { xs: "16px", md: "18px" },
        fontWeight: 700,
        color: "#231F20",
        lineHeight: 1.4,
      }}
    >
      {t("donationPage.alreadyDonated")}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: "14px",
        color: "#231F20",
        mt: 1,
        mb: 2,
        lineHeight: 1.5,
      }}
    >
      <Trans
        i18nKey="donationPage.finishSetupPrompt"
        values={{ email }}
        components={{ 1: <b /> }}
      />
    </Typography>

    <Button
      fullWidth
      onClick={onCompleteRegistration}
      sx={{
        backgroundColor: PURPLE,
        color: "common.white",
        fontFamily: FONT_SANS,
        fontSize: "16px",
        fontWeight: 700,
        textTransform: "none",
        borderRadius: "12px",
        background: PURPLE,
        py: 1.5,
        boxShadow: "0px 4px 4px 0px #00000040",
        "&:hover": { backgroundColor: PURPLE, boxShadow: "0px 4px 4px 0px #00000040" },
      }}
    >
      {t("donationPage.finishSetupMyAccount")}
    </Button>

    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: "14px",
        color: "#231F20",
        mt: 2,
        lineHeight: 1.5,
      }}
    >
      {t("donationPage.notYou")}{" "}
      <Box
        component="span"
        onClick={onUseDifferentEmail}
        sx={{ color: PURPLE, fontWeight: 600, cursor: "pointer" }}
      >
        {t("donationPage.useDifferentEmail")}
      </Box>
    </Typography>
  </Box>
  );
};

/**
 * Greeting for a signed-in donor. Shows the "Inactive Membership" restore
 * message when their plan has lapsed; otherwise a simple active greeting.
 */
const MemberGreeting = ({
  name,
  inactive,
  endDate,
}: {
  name?: string;
  inactive?: boolean;
  endDate?: string | null;
}) => {
  const { t } = useTranslation();
  return (
  <Box sx={{ textAlign: "center"}}>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: { xs: "30px", md: "40px" },
        fontWeight: 800,
        fontStyle: "italic",
        color: "#1A1A40",
        lineHeight: "140%",
      }}
    >
      {t("donationPage.helloName", {
        name: name || t("donationPage.helloThere"),
      })}
    </Typography>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        mb: 1,
      }}
    >
      {/* <Avatar
        src={picture}
        sx={{ width: 28, height: 28, bgcolor: PURPLE, fontSize: "13px" }}
      >
        {(name || "?").charAt(0).toUpperCase()}
      </Avatar> */}
      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: { xs: "26px", md: "32px" },
          fontWeight: 600,
          color: PURPLE,
          lineHeight: "140%",
        }}
      >
        {inactive
          ? t("donationPage.inactiveMembership")
          : t("donationPage.activeMembership")}
      </Typography>
    </Box>

    {inactive ? (
      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: { xs: "14px", md: "16px" },
          fontWeight: 700,
          fontStyle: "italic",
          color: "#1A1A2E",
          lineHeight: 1.5,
          maxWidth: 460,
          mx: "auto",
        }}
      >
        {endDate ? (
          <>
            {t("donationPage.membershipEndedOn", { date: endDate })}
            <br />
            {t("donationPage.restoreBenefits")}
          </>
        ) : (
          <>
            {t("donationPage.noActiveMembershipYet")}
            <br />
            {t("donationPage.unlockBenefits")}
          </>
        )}
      </Typography>
    ) : (
      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: { xs: "15px", md: "16px" },
          fontWeight: 700,
          fontStyle: "italic",
          color: "#1A1A2E",
          lineHeight: 1.5,
          maxWidth: 460,
          mx: "auto",
        }}
      >
        {endDate
          ? t("donationPage.thanksForBeingMemberThrough", { date: endDate })
          : t("donationPage.thanksForBeingMember")}
        <br />
        {t("donationPage.makeAnotherDonation")}
      </Typography>
    )}
  </Box>
  );
};

const PAYMENT_TABS: {
  key: PaymentMethod;
  /** i18n key for the tab label. */
  labelKey: string;
  /** i18n key for the shorter mobile label; falls back to `labelKey`. */
  mobileLabelKey?: string;
  icon: any;
}[] = [
  {
    key: "card", labelKey: "donationPage.card", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.333 3.33325H2.66634C1.92996 3.33325 1.33301 3.93021 1.33301 4.66659V11.3333C1.33301 12.0696 1.92996 12.6666 2.66634 12.6666H13.333C14.0694 12.6666 14.6663 12.0696 14.6663 11.3333V4.66659C14.6663 3.93021 14.0694 3.33325 13.333 3.33325Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.33301 6.66675H14.6663" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  },
  {
    key: "paypal", labelKey: "donationPage.paypal", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_1861_1289)">
        <path d="M4.66634 11.9999H1.33301L3.99967 1.33325H8.66634C9.6388 1.33325 10.5714 1.71956 11.2591 2.40719C11.9467 3.09483 12.333 4.02746 12.333 4.99992C12.333 5.97238 11.9467 6.90501 11.2591 7.59264C10.5714 8.28028 9.6388 8.66659 8.66634 8.66659H5.53301M3.99967 14.6666L6.66634 3.99992H11.333C12.3055 3.99992 13.2381 4.38623 13.9257 5.07386C14.6134 5.76149 14.9997 6.69413 14.9997 7.66659C14.9997 8.63905 14.6134 9.57168 13.9257 10.2593C13.2381 10.9469 12.3055 11.3333 11.333 11.3333H8.19967L7.33301 14.6666H3.99967Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_1861_1289">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  },
  {
    key: "checking", labelKey: "donationPage.checking", mobileLabelKey: "donationPage.checkingShort", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.6667 4.66667V2.66667C12.6667 2.48986 12.5964 2.32029 12.4714 2.19526C12.3464 2.07024 12.1768 2 12 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333C2 3.68696 2.14048 4.02609 2.39052 4.27614C2.64057 4.52619 2.97971 4.66667 3.33333 4.66667H13.3333C13.5101 4.66667 13.6797 4.7369 13.8047 4.86193C13.9298 4.98695 14 5.15652 14 5.33333V8M14 8H12C11.6464 8 11.3072 8.14048 11.0572 8.39052C10.8071 8.64057 10.6667 8.97971 10.6667 9.33333C10.6667 9.68696 10.8071 10.0261 11.0572 10.2761C11.3072 10.5262 11.6464 10.6667 12 10.6667H14C14.1768 10.6667 14.3464 10.5964 14.4714 10.4714C14.5964 10.3464 14.6667 10.1768 14.6667 10V8.66667C14.6667 8.48986 14.5964 8.32029 14.4714 8.19526C14.3464 8.07024 14.1768 8 14 8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 3.33325V12.6666C2 13.0202 2.14048 13.3593 2.39052 13.6094C2.64057 13.8594 2.97971 13.9999 3.33333 13.9999H13.3333C13.5101 13.9999 13.6797 13.9297 13.8047 13.8047C13.9298 13.6796 14 13.5101 14 13.3333V10.6666" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  },
];

const formatAmount = (value: number) => `$${value.toLocaleString("en-US")}`;

const CheckRow = ({
  checked,
  onToggle,
  children,
  disabled = false,
}: {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Checkbox
      checked={checked}
      onChange={onToggle}
      disabled={disabled}
      disableRipple
      size="small"
      sx={{
        p: 0,
        mt: "1px",
        color: "#C4C2BA",
        "&.Mui-checked": { color: PURPLE },
        "&.Mui-disabled": { color: "#C4C2BA", opacity: 0.5 },
      }}
    />
    <Typography
      sx={{
        fontFamily: "DM Sans",
        fontSize: {xs: "12px", md: "14px"},
        color: "#231F20",
        lineHeight: 1.4,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </Typography>
  </Box>
);

/**
 * Left-hand donation form for the redesign. Fully controlled by the parent
 * section; this component is presentational and gathers the donor's choices.
 */
const DonationForm = ({
  state,
  onChange,
  onSubmit,
  effectiveAmount,
  showAutoRenew,
  variant,
  onSignIn,
  onEmailBlur,
  checkingEmail,
  onUseDifferentEmail,
  onCompleteRegistration,
  signingIn,
  authError,
  onCardChange,
  onExpiryChange,
  onCvcChange,
  achReady,
  achConnectedMask,
  onConnectBank,
  submitting,
  submitError,
  donorName,
  membershipInactive,
  membershipEndDate,
  infoBelowAmount,
  amountError,
  trialMode,
}: DonationFormProps) => {
  const { t } = useTranslation();
  const amounts = getAmountsForFrequency(state.frequency);
  const coverFee = calcCoverFee(effectiveAmount);

  /** Gifting the plan to someone else requires at least GIFT_MINIMUM dollars. */
  const giftingAllowed = effectiveAmount >= GIFT_MINIMUM;

  // Dropping below the minimum after ticking the box clears the choice, so the
  // state can't hold a gift selection the amount no longer qualifies for.
  React.useEffect(() => {
    if (!giftingAllowed && state.giftToSomeone) {
      onChange("giftToSomeone", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftingAllowed, state.giftToSomeone]);
  // Mirror the parent's derived values so the mobile-only summary matches the
  // desktop one rendered in DonateSection.
  const additionalAmount = state.coverFees ? coverFee : 0;
  const autoRenewActive = showAutoRenew && state.autoRenew;

  // Top-of-form block, chosen by the donor's Auth0 state. Replaces the static
  // "Your Information" section. In the V3 layout this renders below the amount
  // block (see {@link infoBelowAmount}).
  const infoBlock =
    variant === "member" ? (
      <MemberGreeting
        name={donorName}
        inactive={membershipInactive}
        endDate={membershipEndDate}
      />
    ) : variant === "sign-in" ? (
      <RecognizedSignIn
        email={state.email}
        onSignIn={onSignIn}
        onUseDifferentEmail={onUseDifferentEmail}
        error={authError}
        loading={signingIn}
      />
    ) : variant === "complete-registration" ? (
      <CompleteRegistrationPrompt
        email={state.email}
        onCompleteRegistration={onCompleteRegistration}
        onUseDifferentEmail={onUseDifferentEmail}
      />
    ) : (
      <NewVisitorInfo
        email={state.email}
        onEmailChange={(value) => onChange("email", value)}
        onEmailBlur={onEmailBlur}
        onSignIn={onSignIn}
        signingIn={signingIn}
        checking={checkingEmail}
        error={authError}
      />
    );

  // CHOOSE DONATION AMOUNT
  const amountBlock = (
    <Box>
      <SectionHeading>{t("donationPage.chooseDonationAmount")}</SectionHeading>

      {/* One-Time / Monthly toggle */}
      <Box
        sx={{
          display: "flex",
          p: "6px",
          mb: {xs: 2.7, md: 3.1},
          // border: "1px solid #D9D7D0",
          boxShadow: "0px 4px 4px 0px #00000040",
          borderRadius: "12px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {(["one-time", "monthly"] as DonationFrequency[]).map((freq) => {
          const selected = state.frequency === freq;
          return (
            <ButtonBase
              key={freq}
              onClick={() => onChange("frequency", freq)}
              sx={{
                flex: 1,
                py: 1.1,
                borderRadius: "8px",
                fontFamily: FONT_SANS,
                fontSize: {xs:"14px", md: "16px"},
                fontWeight: 400,
                lineHeight: 1.3,
                color: selected ? "common.white" : "#4A4852",
                backgroundColor: selected ? PURPLE : "transparent",
                transition: "background-color .2s ease, color .2s ease",
              }}
            >
              {freq === "one-time"
                ? t("donationPage.oneTime")
                : t("donationPage.monthly")}
            </ButtonBase>
          );
        })}
      </Box>

      {/* Amount grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)"},
          columnGap: {xs: 5.5, md: 1.5},
          rowGap: {xs: 3.7, md: 3},
        }}
      >
        {amounts.map((value) => {
          const selected = state.amount === value && !state.otherAmount;
          return (
            <Box
              key={value}
              sx={{
                position: "relative",
                // Reveal the membership badge while hovering the button too.
                "&:hover .jg-amount-badge": { display: "block" },
              }}
            >
              <ButtonBase
                onClick={() => {
                  onChange("amount", value);
                  onChange("otherAmount", "");
                }}
                sx={{
                  width: "100%",
                  py: 1.3,
                  borderRadius: "12px",
                  fontFamily: FONT_SANS,
                  fontSize: "22px",
                  fontWeight: 700,
                  color: selected ? "common.white" : "#1A1520",
                  backgroundColor: selected ? PURPLE : "#FFFFFF",
                  border: `${selected ? "2px" : "1px"} solid ${selected ? "#4A3F35" : "#E2D5C4"}`,
                  transition: "background-color .15s ease, border-color .15s ease",
                  boxShadow: "0px 4px 4px 0px #00000040",
                }}
              >
                {formatAmount(value)}
              </ButtonBase>
              {/* Monthly has no badge: the label counts the months a one-time
                  donation buys ($10 each), which says nothing about a monthly
                  donation — that one renews a month at a time. */}
              {state.frequency !== "monthly" && (
                <Box
                  className="jg-amount-badge"
                  sx={{
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#3F3F3F",
                    border: "0.5px solid #F6B60B",
                    borderRadius: "8px",
                    px: 1,
                    py: "3px",
                    whiteSpace: "nowrap",
                    // Shown for the selected amount; also on hover (see wrapper).
                    display: selected ? "block" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_SANS,
                      fontSize: "10px",
                      fontWeight: 500,
                      color: "#F6B60B",
                      lineHeight: 1,
                    }}
                  >
                    {getMembershipTooltip(value, t)}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Other amount */}
      <Box sx={{ ...fieldSx, display: "flex", alignItems: "center", mt: {xs: 5.5, md: 4}, py: 0 }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: "15px", color: "#9A988F", mr: 0.5 }}>
          $
        </Typography>
        <InputBase
          value={state.otherAmount}
          onChange={(e) => onChange("otherAmount", e.target.value.replace(/[^0-9]/g, ""))}
          placeholder={t("donationPage.otherAmountsPlaceholder")}
          inputMode="numeric"
          sx={{ flex: 1, py: 1.13, fontFamily: FONT_SANS, fontSize: "16px", color: "#1A1721" }}
        />
      </Box>
      {/* Below-minimum custom amount, shown as you type — the legacy checkout's
          DonationAmountStep does the same. Presets are all above the minimum, so
          this can only ever apply to a typed amount. */}
      {amountError && (
        <Typography
          sx={{
            fontFamily: FONT_SANS,
            fontSize: { xs: "12px", md: "13px" },
            color: "#D32F2F",
            mt: 1,
          }}
        >
          {amountError}
        </Typography>
      )}
    </Box>
  );

  // Paid-but-not-registered donor: they already paid, so show ONLY the "finish
  // setting up your account" prompt — not the donation form (no re-payment).
  if (variant === "complete-registration") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {infoBlock}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* V2: info above amount. V3: amount above info. A free trial has a fixed
          $10/month price, so the amount block is dropped entirely rather than
          shown with a single locked option. */}
      {trialMode ? (
        infoBlock
      ) : infoBelowAmount ? (
        <>
          {amountBlock}
          {infoBlock}
        </>
      ) : (
        <>
          {infoBlock}
          {amountBlock}
        </>
      )}

      {/* summary — mobile only (the desktop copy lives in DonateSection) */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <DonationSummaryV2
          frequency={state.frequency}
          amount={effectiveAmount}
          additionalAmount={additionalAmount}
          autoRenew={autoRenewActive}
          trialMode={trialMode}
        />
      </Box>

      {/* PAYMENT INFORMATION */}
      <Box>
        <SectionHeading>{t("donationPage.paymentInformation")}</SectionHeading>

        {/* Free trial is card only: the card is stored and billed when the trial
            ends. PayPal and bank checkout charge up front, so their tabs are
            hidden; the form starts on "card" and with no tabs can't leave it. */}
        {!trialMode && (
        <Box sx={{ display: "flex", gap: {xs:1,md: 2.4}, mb: {xs: 1.8,md: 3} }}>
          {PAYMENT_TABS.map(({ key, labelKey, mobileLabelKey, icon }) => {
            const selected = state.paymentMethod === key;
            return (
              <ButtonBase
                key={key}
                onClick={() => onChange("paymentMethod", key)}
                sx={{
                  flex: 1,
                  gap: 1,
                  py: 1.8,
                  pl:2,
                  pr:{xs:1.2, md:2},
                  borderRadius: "8px",
                  fontFamily: FONT_SANS,
                  fontSize: {xs: "12px", md: "16px"},
                  fontWeight: 400,
                  color: selected ? "common.white" : "#4A4852",
                  backgroundColor: selected ? PURPLE : "#FFFFFF",
                  border: `1px solid ${selected ? PURPLE : "#DDDDDD"}`,
                  boxShadow: "0px 4px 4px 0px #00000040",
                  justifyContent:"left"
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    lineHeight: 0,
                    color: selected ? "#FFFFFF" : "#000000",
                  }}
                >
                  {icon}
                </Box>
                {mobileLabelKey ? (
                  <>
                    <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                      {t(labelKey)}
                    </Box>
                    <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                      {t(mobileLabelKey)}
                    </Box>
                  </>
                ) : (
                  t(labelKey)
                )}
              </ButtonBase>
            );
          })}
        </Box>
        )}

        {state.paymentMethod === "card" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: {xs: 1.8,md: 3} }}>
            <StripeTextField
              fullWidth
              stripeElement={CardNumberElement}
              onChange={(e) => onCardChange?.(!!e.complete)}
              InputProps={{
                startAdornment: (
                  <JGIcon is={IoCardOutline} sx={{ fontSize: 18, color: "#9A988F", mr: 1 }} />
                ),
                inputProps: stripeInputProps,
              }}
              sx={stripeFieldSx}
            />
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <StripeTextField
                fullWidth
                stripeElement={CardExpiryElement}
                onChange={(e) => onExpiryChange?.(!!e.complete)}
                InputProps={{ inputProps: stripeInputProps }}
                sx={stripeFieldSx}
              />
              <StripeTextField
                fullWidth
                stripeElement={CardCvcElement}
                onChange={(e) => onCvcChange?.(!!e.complete)}
                InputProps={{ inputProps: stripeInputProps }}
                sx={stripeFieldSx}
              />
            </Box>
            {/* Billing ZIP — trial only. Stripe's split card elements have no
                postal-code field, and the trial stores the card for a later
                charge, so the ZIP is sent with the token for the AVS check. */}
            {trialMode && (
              <TextField
                fullWidth
                value={state.zip}
                onChange={(e) => onChange("zip", e.target.value)}
                placeholder={t("donationPage.zipPlaceholder")}
                inputProps={{ inputMode: "numeric", autoComplete: "postal-code", maxLength: 10 }}
                sx={stripeFieldSx}
              />
            )}
          </Box>
        ) : state.paymentMethod === "checking" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1 }}>
            {achConnectedMask ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 2,
                  py: 1.6,
                  borderRadius: "8px",
                  border: "1px solid #99A1AF",
                  backgroundColor: "#F7F6F3",
                }}
              >
                <Typography sx={{ fontFamily: FONT_SANS, fontSize: "14px", color: "#3F3F3F" }}>
                  {t("donationPage.bankConnected", { mask: achConnectedMask })}
                </Typography>
                <ButtonBase
                  onClick={onConnectBank}
                  disabled={!achReady}
                  sx={{ fontFamily: FONT_SANS, fontSize: "13px", fontWeight: 600, color: PURPLE }}
                >
                  {t("donationPage.change")}
                </ButtonBase>
              </Box>
            ) : (
              <ButtonBase
                onClick={onConnectBank}
                disabled={!achReady}
                sx={{
                  py: 1.8,
                  borderRadius: "8px",
                  border: `1px solid ${PURPLE}`,
                  color: PURPLE,
                  fontFamily: FONT_SANS,
                  fontSize: "14px",
                  fontWeight: 600,
                  opacity: achReady ? 1 : 0.6,
                }}
              >
                {achReady ? t("donationPage.connectBankAccount") : t("donationPage.loadingBankConnection")}
              </ButtonBase>
            )}
            <Typography sx={{ fontFamily: FONT_SANS, fontSize: "12px", color: "#6B6973", textAlign: "center" }}>
              {t("donationPage.plaidSecureNote")}
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: "14px",
              color: "#6B6973",
              textAlign: "center",
              py: 2,
            }}
          >
            {t("donationPage.paypalAfterSubmit")}
          </Typography>
        )}

        {/* A free trial has no options to offer — no fees to cover (nothing is
            charged today) and auto-renew never shows for monthly — so the
            divider and the empty options block are dropped with it. */}
        {!trialMode && (
        <>
        <Box sx={{ flexGrow: 1, height: "1px", backgroundColor: PURPLE,my:{xs: 5.3,md: 3}}} />

        {/* Options */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Hidden for now — ticking it did nothing: `giftToSomeone` is never
              read, and both submit paths send `isGift: false` hard-coded, so the
              donor got an ordinary donation for themselves and no recipient was
              ever collected. Kept here (with its GIFT_MINIMUM gate) for when the
              recipient form is built and wired to the lambda's existing
              chargebee.gift.create branch.
          <CheckRow
            checked={state.giftToSomeone}
            onToggle={() => onChange("giftToSomeone", !state.giftToSomeone)}
            disabled={!giftingAllowed}
          >
            {t("donationPage.giftToAnotherMin", { min: GIFT_MINIMUM })}
          </CheckRow>
          */}
          <CheckRow
            checked={state.coverFees}
            onToggle={() => onChange("coverFees", !state.coverFees)}
          >
            {t("donationPage.coverFeesV2", { fee: coverFee.toFixed(2) })}
          </CheckRow>
          {showAutoRenew && (
            <CheckRow
              checked={state.autoRenew}
              onToggle={() => onChange("autoRenew", !state.autoRenew)}
            >
              {t("donationPage.autoRenewCheckbox")}
            </CheckRow>
          )}
        </Box>
        </>
        )}
      </Box>

      {/* Submit */}
      <Box>
        {/* Reassurance at the moment of commitment. The price lives only in the
            summary, so this says nothing else. */}
        {trialMode && (
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: { xs: "12px", md: "13px" },
              color: "#231F20",
              textAlign: "center",
              mb: 1.5,
            }}
          >
            {t("donationPage.trialFinePrint")}
          </Typography>
        )}
        <Button
          fullWidth
          onClick={onSubmit}
          disabled={submitting}
          // The heart belongs to "Donate"; the trial button is a plain "Go To Final Step!".
          startIcon={trialMode ? undefined : <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.6663 9.33333C13.6597 8.36 14.6663 7.19333 14.6663 5.66667C14.6663 4.69421 14.28 3.76158 13.5924 3.07394C12.9048 2.38631 11.9721 2 10.9997 2C9.82634 2 8.99967 2.33333 7.99967 3.33333C6.99967 2.33333 6.17301 2 4.99967 2C4.02721 2 3.09458 2.38631 2.40695 3.07394C1.71932 3.76158 1.33301 4.69421 1.33301 5.66667C1.33301 7.2 2.33301 8.36667 3.33301 9.33333L7.99967 14L12.6663 9.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          }

          sx={{
            backgroundColor: PURPLE,
            color: "common.white",
            fontFamily: FONT_SANS,
            fontSize: "16px",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "12px",
            background: PURPLE,
            py: 1.5,
            boxShadow: "0px 4px 4px 0px #00000040",
            "&:hover": { backgroundColor: PURPLE, boxShadow: "0px 4px 4px 0px #00000040" },
            // The button is disabled while the donation is processing. MUI's own
            // disabled styles drop the background, so it has to be re-asserted here
            // or the button turns transparent mid-submit — dimming is the intent,
            // not disappearing.
            "&.Mui-disabled": {
              color: "common.white",
              opacity: 0.7,
              backgroundColor: PURPLE,
              background: PURPLE,
            },
          }}
        >
          {submitting
            ? t("donationPage.processing")
            : t(trialMode ? "donationPage.trialSubmit" : "donationPage.submitDonation")}
        </Button>
        {submitError && (
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: "13px",
              color: "#D32F2F",
              textAlign: "center",
              mt: 1,
            }}
          >
            {submitError}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            mt: 1.5,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1278_7661)">
            <path d="M9.5 5.5H2.5C1.94772 5.5 1.5 5.94772 1.5 6.5V10C1.5 10.5523 1.94772 11 2.5 11H9.5C10.0523 11 10.5 10.5523 10.5 10V6.5C10.5 5.94772 10.0523 5.5 9.5 5.5Z" stroke="#1A1520" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5" stroke="#1A1520" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            <defs>
            <clipPath id="clip0_1278_7661">
            <rect width="12" height="12" fill="white"/>
            </clipPath>
            </defs>
            </svg>

          <Typography sx={{ fontFamily: FONT_SANS, fontSize: "12px", color: "#1A1520",letterSpacing:"1px" }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {t("donationPage.secureTransaction")}
            </Box>{" "}
            {t("donationPage.paymentEncrypted")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DonationForm;
